import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authContext';

const API = '/api';

const NOMBRES_TABLAS = {
  form_b_tramitacion: 'formB — Tramitación',
  form_c_seguimiento_obras: 'formC — Seguimiento',
  form_b_expediente_proyecto: 'formB — Expediente de Proyecto',
  microcreditos_beneficiarios: 'Microcréditos — Beneficiarios',
  form_c_mediciones_mejoras_etapa_i: 'formC — Mediciones Mejoras Etapa I',
  form_c_mediciones_mejoras_etapa_ii: 'formC — Mediciones Mejoras Etapa II',
  form_c_circuito_administrativo: 'formC — Circuito Administrativo',
  form_b_observaciones_internas: 'formB — Observaciones Internas',
  mediciones_obra: 'Mediciones de Obra',
  geometrias_obras: 'Geometrías',
};

const CHILD_FIELD_LABELS = {
  documentos_seleccionados: 'Requisitos',
  tramitacion_interna_jsonb: 'Tramitación interna',
  tramitacion_externa_jsonb: 'Tramitación externa',
  observaciones_reporte: 'Observaciones reporte',
  observaciones_internas: 'Observaciones internas',
  observaciones_externas: 'Observaciones externas',
  pago_mejoras_jsonb: 'Pago mejoras',
  pago_microcreditos_jsonb: 'Pago microcréditos',
  pago_proyectos_jsonb: 'Pago proyectos',
  observ_seguimiento_obra: 'Observaciones seguimiento',
};

function fmtArray(v) {
  if (!Array.isArray(v) || v.length === 0) return null;
  const names = v.map(r => {
    const n = r.name || r.objeto_o || r.nombre_beneficiario || r.descripcion || '';
    if (n) return n;
    const vals = Object.values(r).filter(v => typeof v === 'string' && v.length > 0);
    if (vals.length > 0) return vals[0].slice(0, 40);
    const booleanVals = Object.entries(r).filter(([, v]) => v === true);
    if (booleanVals.length > 0) return booleanVals.map(([k]) => k).join(', ');
    return '(en blanco)';
  });
  return names.join(' | ').slice(0, 120);
}

function deepNormalize(v) {
  if (v === null || v === undefined) return v;
  if (Array.isArray(v)) return v.map(deepNormalize);
  if (typeof v === 'object') {
    const { valorOriginal, ...rest } = v;
    const sorted = {};
    Object.keys(rest).sort().forEach(k => {
      sorted[k] = deepNormalize(rest[k]);
    });
    return sorted;
  }
  return v;
}

function diffFields(actual, anterior) {
  if (!actual || !anterior) return [];
  const normalizar = v => {
    if (v === null || v === undefined) return null;
    if (Array.isArray(v) && v.length === 0) return null;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return null;
    return deepNormalize(v);
  };
  const changes = [];
  const allKeys = new Set([...Object.keys(actual), ...Object.keys(anterior)]);
  const skipKeys = new Set(['id', 'version', 'historial_valid_since', 'fecha_actualizacion', 'fecha_carga', 'id_obra', 'id_origen', 'activo']);
  const fullJsonKeys = new Set(['obras_expediente_jsonb', 'tipo_subtipo_proyecto_jsonb']);
  for (const key of allKeys) {
    if (skipKeys.has(key)) continue;
    const a = normalizar(actual[key]);
    const b = normalizar(anterior[key]);
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      const isFullJson = fullJsonKeys.has(key) && (Array.isArray(a) || Array.isArray(b));
      const fmt = v => {
        if (v === null || v === undefined) return '(vacío)';
        if (isFullJson) {
          return JSON.stringify(v, null, 2);
        }
        return fmtArray(v) ?? (typeof v === 'object' ? JSON.stringify(v).slice(0, 120) : String(v));
      };
      changes.push({ key, actual: fmt(a), anterior: fmt(b), fullJson: isFullJson });
    }
  }
  return changes;
}

function diffChildField(key, aVal, bVal) {
  const a = deepNormalize(aVal);
  const b = deepNormalize(bVal);
  const sa = JSON.stringify(a);
  const sb = JSON.stringify(b);
  if (sa !== sb) {
    const fmt = v => fmtArray(v) ?? (v && typeof v === 'object' ? JSON.stringify(v).slice(0, 120) : (v ?? '(vacío)'));
    return { key, actual: fmt(aVal), anterior: fmt(bVal) };
  }
  return null;
}

function formatearValor(v) {
  if (v === true) return 'Sí';
  if (v === false) return 'No';
  if (v === null || v === undefined) return '(vacío)';
  if (typeof v === 'object') {
    if (Array.isArray(v) && v.length === 0) return '(vacío)';
    if (!Array.isArray(v) && Object.keys(v).length === 0) return '(vacío)';
    return JSON.stringify(v, null, 2);
  }
  return String(v);
}

function diffChildSub(actual, anterior) {
  if (!actual || !anterior) return [];
  const campos = Object.keys({...actual, ...anterior});
  const skip = ['id', 'id_original', 'archived_at', 'operation', 'id_tabla_obras', 'from_cg_01_id_partido_obra', 'version_global', 'version', 'fecha_creacion', 'created_at', 'updated_at', 'deleted_at', 'activo'];
  const changes = [];
  for (const k of campos) {
    if (skip.includes(k)) continue;
    const d = diffChildField(k, actual[k], anterior[k]);
    if (d) changes.push(d);
  }
  return changes;
}

const RECORD_SKIP = ['id', 'id_original', 'id_tabla_obras', 'from_cg_01_id_partido_obra', 'version_global', 'archived_at', 'operation', 'version', 'fecha_creacion', 'created_at', 'updated_at', 'deleted_at', 'activo'];
const TABLAS_AGRUPADAS = ['mediciones_obra'];

function fmtRec(r) {
  return Object.entries(r).filter(([k]) => !RECORD_SKIP.includes(k)).map(([k, v]) => `${k}: ${v !== null && typeof v === 'object' ? JSON.stringify(v) : v ?? '—'}`).join('\n');
}

function diffChildTables(actual, anterior) {
  if (!actual || !anterior) return [];
  const names = ['form_b_tramitacion', 'form_c_seguimiento_obras', 'form_b_expediente_proyecto',
    'microcreditos_beneficiarios', 'form_c_mediciones_mejoras_etapa_i', 'form_c_mediciones_mejoras_etapa_ii',
    'form_c_circuito_administrativo', 'form_b_observaciones_internas', 'mediciones_obra', 'geometrias_obras'];
  const changes = [];
  for (const t of names) {
    const a = actual[t];
    const b = anterior[t];
    if (a === b) continue;
    if (!a && !b) continue;
    if (Array.isArray(a) && Array.isArray(b)) {
      const getId = r => r.name || r.nombre_beneficiario || r.descripcion || r.nro_medicion || r.circuito || r.observaciones || '';
      const hasId = [...a, ...b].some(r => getId(r));
      if (!hasId) {
        if (a.length !== b.length) {
          const fmtRecord = items => items.map(r => {
            const parts = Object.entries(r).filter(([k]) => !RECORD_SKIP.includes(k)).map(([k, v]) => `${k}: ${v !== null && typeof v === 'object' ? JSON.stringify(v) : v ?? '—'}`);
            return `{ ${parts.join(', ')} }`;
          }).join('\n');
          changes.push({ table: t, item: '', campo: '(cantidad)', actual: `${a.length} registro(s):\n${fmtRecord(a)}`, anterior: `${b.length} registro(s):\n${fmtRecord(b)}` });
        }
        continue;
      }
      const idxA = {}; for (const r of a) { const id = getId(r); if (id) idxA[id] = r; }
      const idxB = {}; for (const r of b) { const id = getId(r); if (id) idxB[id] = r; }
      const allIds = [...new Set([...Object.keys(idxA), ...Object.keys(idxB)])].sort();
      for (const id of allIds) {
        if (id === '(sin nombre)' || id === '') continue;
        const ra = idxA[id];
        const rb = idxB[id];
        if (ra && !rb) {
          const val = ra.is_checked != null ? (ra.is_checked ? 'Sí' : 'No') : null;
          changes.push({ table: t, item: id, campo: null, actual: val ?? fmtRec(ra), anterior: '---' });
        } else if (!ra && rb) {
          const val = rb.is_checked != null ? (rb.is_checked ? 'Sí' : 'No') : null;
          changes.push({ table: t, item: id, campo: null, actual: '---', anterior: val ?? fmtRec(rb) });
        } else if (TABLAS_AGRUPADAS.includes(t)) {
          const aRec = fmtRec(ra);
          const bRec = fmtRec(rb);
          if (aRec !== bRec) changes.push({ table: t, item: id, campo: null, actual: aRec, anterior: bRec });
        } else {
          const sub = diffChildSub(ra, rb);
          for (const d of sub) {
            changes.push({ table: t, item: id, campo: d.key, actual: d.actual, anterior: d.anterior });
          }
        }
      }
    } else if (typeof a === 'object' && !Array.isArray(a) && typeof b === 'object' && !Array.isArray(b)) {
      const sub = diffChildSub(a, b);
      for (const d of sub) {
        changes.push({ table: t, item: '(general)', campo: d.key, actual: d.actual, anterior: d.anterior });
      }
    } else {
      const arr = Array.isArray(a) ? a : (Array.isArray(b) ? b : null);
      if (arr && arr.some(r => r.name || r.nombre_beneficiario || r.descripcion || r.nro_medicion || r.circuito)) {
        const safeA = Array.isArray(a) ? a : [];
        const safeB = Array.isArray(b) ? b : [];
        const getId = r => r.name || r.nombre_beneficiario || r.descripcion || r.nro_medicion || r.circuito || r.observaciones || '';
        const idxA = {}; for (const r of safeA) { const id = getId(r); if (id) idxA[id] = r; }
        const idxB = {}; for (const r of safeB) { const id = getId(r); if (id) idxB[id] = r; }
        const allIds = [...new Set([...Object.keys(idxA), ...Object.keys(idxB)])].sort();
        for (const id of allIds) {
          if (!id) continue;
          const ra = idxA[id];
          const rb = idxB[id];
          if (ra && !rb) {
            const val = ra.is_checked != null ? (ra.is_checked ? 'Sí' : 'No') : null;
            changes.push({ table: t, item: id, campo: null, actual: val ?? fmtRec(ra), anterior: '---' });
          } else if (!ra && rb) {
            const val = rb.is_checked != null ? (rb.is_checked ? 'Sí' : 'No') : null;
            changes.push({ table: t, item: id, campo: null, actual: '---', anterior: val ?? fmtRec(rb) });
          } else if (TABLAS_AGRUPADAS.includes(t)) {
            const aRec = fmtRec(ra);
            const bRec = fmtRec(rb);
            if (aRec !== bRec) changes.push({ table: t, item: id, campo: null, actual: aRec, anterior: bRec });
          } else {
            const sub = diffChildSub(ra, rb);
            for (const d of sub) changes.push({ table: t, item: id, campo: d.key, actual: d.actual, anterior: d.anterior });
          }
        }
      } else {
        const fmt = v => Array.isArray(v) ? `${v.length} registros` : (v ?? 'vacío');
        changes.push({ table: t, item: '', campo: '(valor)', actual: fmt(a), anterior: fmt(b) });
      }
    }
  }
  return changes;
}

const FIELD_LABELS = {
  cg_01_id_partido_obra: 'Partido',
  cg_05_fecha_actualizacion: 'Fecha actualización',
  cg_10_codigo_obra: 'Código obra',
  cg_16_estado: 'Estado',
  encargado_proyecto: 'Encargado del proyecto',
  area_responsable: 'Área responsable',
  tipo_gestion: 'Tipo de gestión',
  tipo_obra: 'Tipo de obra',
  cg_09_tipo_obra_detalle: 'Tipo obra detalle',
  cg_12_plazo_ejecucion_meses: 'Plazo ejecución (meses)',
  monto_contrato: 'Monto contrato',
  monto_contrato_updated_at: 'Monto contrato actualizado',
  cantidad_mano_obra: 'Cantidad mano de obra',
  cantidad_mano_obra_updated_at: 'Cantidad mano de obra actualizado',
  fecha_inicio: 'Fecha inicio',
  fecha_fin_estimada: 'Fecha fin estimada',
  cg_11_destino: 'Destino',
  cg_17_tipo_contratacion: 'Tipo contratación',
  cg_18_empresa_ejecuta: 'Empresa ejecuta',
  programa_origen: 'Programa origen',
  cg_20_expediente: 'Expediente',
  cg_21_expediente: 'Expediente (2)',
  observaciones: 'Observaciones',
  coordenadas: 'Coordenadas',
  habilitar_multiples_partidos: 'Habilitar múltiples partidos',
  cg_19_codigo_obra_alternativo: 'Código obra alternativo',
  partidos_seleccionados: 'Partidos seleccionados',
  barrios_seleccionados: 'Barrios seleccionados',
  obras_expediente_jsonb: 'Expediente',
  tipo_subtipo_proyecto_jsonb: 'Tipo Subtipo Proyecto',
  desde_que_se_hizo_el_plan: 'Desde que se hizo el plan',
  id_etapa1_referencia: 'ID Etapa 1 referencia',
  tipo_microcredito: 'Tipo microcrédito',
};

function formatearValorModal(v) {
  if (v === true || v === 'true') return 'Sí';
  if (v === false || v === 'false') return 'No';
  if (v === null || v === undefined || v === '') return '(vacío)';
  if (Array.isArray(v)) {
    if (v.length === 0) return '(vacío)';
    return JSON.stringify(v, null, 2);
  }
  if (typeof v === 'object') {
    if (Object.keys(v).length === 0) return '(vacío)';
    return JSON.stringify(v, null, 2);
  }
  return String(v);
}

function renderObraFields(obras) {
  if (!obras) return [];
  const skip = new Set(['id', 'version', 'historial_valid_since', 'fecha_carga', 'activo', 'cg_05_fecha_actualizacion']);
  return Object.entries(obras)
    .filter(([k]) => !skip.has(k) && !k.startsWith('id_'))
    .map(([k, v]) => ({
      label: FIELD_LABELS[k] || k,
      valor: formatearValorModal(v),
    }));
}

export default function VersionHistory({ obraId, cg01IdPartidoObra }) {
  const { token, role } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [reconstruccion, setReconstruccion] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [modalVersion, setModalVersion] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  useEffect(() => {
    if (!obraId || !abierto) return;
    setCargando(true);
    setError('');
    setReconstruccion(null);
    setSelectedVersion(null);
    setModalData(null);
    setModalVersion(null);
    fetch(`${API}/obras/${obraId}/versions`, { headers })
      .then(r => r.json())
      .then(d => { setData(d); setCargando(false); })
      .catch(() => { setError('Error al cargar versiones'); setCargando(false); });
  }, [obraId, abierto, token, refreshKey]);

  const handleVerVersion = async (v) => {
    if (!data?.version_actual) return;
    setCargando(true);
    setError('');
    setSelectedVersion(v);
    setReconstruccion(null);
    try {
      const [rHist, rActual] = await Promise.all([
        fetch(`${API}/obras/${obraId}/versions/${v}`, { headers }),
        fetch(`${API}/obras/${obraId}/versions/${data.version_actual}`, { headers }),
      ]);
      const dHist = await rHist.json();
      const dActual = await rActual.json();
      if (dHist.error) { setError(dHist.error); return; }
      setReconstruccion({ historico: dHist, actual: dActual });
    } catch { setError('Error al reconstruir versión'); }
    setCargando(false);
  };

  const handleVerCompletos = async (v) => {
    setCargando(true);
    setError('');
    try {
      const r = await fetch(`${API}/obras/${obraId}/versions/${v}`, { headers });
      const d = await r.json();
      if (d.error) { setError(d.error); setModalData(null); setModalVersion(null); }
      else { setModalData(d); setModalVersion(v); }
    } catch { setError('Error al cargar versión completa'); }
    setCargando(false);
  };

  const handleRestaurar = async (v) => {
    if (!window.confirm(`¿Restaurar obra a versión ${v}? Se creará una nueva versión.`)) return;
    if (!window.confirm(`Última confirmación: restaurar a versión ${v}?`)) return;
    setCargando(true);
    setError('');
    try {
      const r = await fetch(`${API}/obras/${obraId}/versions/${v}/restore`, {
        method: 'POST',
        headers,
      });
      const d = await r.json();
      if (d.error) { setError(d.error); } else {
        alert(`Obra restaurada a v${v}. Nueva versión: ${d.version_nueva}. Recargando...`);
        window.location.reload();
      }
    } catch { setError('Error al restaurar versión'); }
    setCargando(false);
  };

  if (role !== 'admin') return null;

  const cambios = reconstruccion?.historico?.obras && reconstruccion?.actual?.obras
    ? diffFields(reconstruccion.actual.obras, reconstruccion.historico.obras)
    : [];
  const cambiosChild = reconstruccion?.actual && reconstruccion?.historico
    ? diffChildTables(reconstruccion.actual, reconstruccion.historico)
    : [];

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => { setAbierto(!abierto); if (!abierto) { setReconstruccion(null); setSelectedVersion(null); } }}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-gray-700 border-b border-gray-200"
      >
        <span>Historial de versiones</span>
        <svg className={`w-4 h-4 transition-transform ${abierto ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {abierto && (
        <div>
          {/* Lista de versiones: altura fija ~5 items, scroll */}
          <div className="overflow-y-auto border-b border-gray-200" style={{maxHeight: '14rem'}}>
            {cargando && <p className="text-gray-400 text-sm text-center py-4">Cargando...</p>}
            {error && <p className="text-red-500 text-xs mx-3 mt-2">{error}</p>}
            {data && (
              <div className="p-3 space-y-1">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400">
                    Versión actual: <span className="font-mono font-semibold">{data.version_actual}</span>
                    {data.historial_valid_since && (
                      <span className="ml-2 text-gray-300">
                        (historial completo desde v{data.historial_valid_since})
                      </span>
                    )}
                  </p>
                  <button
                    onClick={() => { setRefreshKey(k => k + 1); setReconstruccion(null); setSelectedVersion(null); setModalData(null); setModalVersion(null); }}
                    className="text-[10px] px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-500 font-medium"
                    title="Recargar historial"
                  >
                    recargar
                  </button>
                </div>
                {data.versiones.map((v) => {
                  const isSelected = selectedVersion === v.version;
                  return (
                    <div
                      key={v.version}
                      className={`flex items-center justify-between px-3 py-2 rounded text-sm border ${
                        v.es_actual ? 'bg-blue-50 border-blue-200' :
                        isSelected ? 'bg-amber-50 border-amber-300' :
                        'border-transparent hover:bg-gray-50'
                      } ${v.restaurable ? '' : 'opacity-50'}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-medium text-gray-500 w-12">v{v.version}</span>
                        {v.es_actual && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">actual</span>
                        )}
                        {!v.restaurable && !v.es_actual && (
                          <span className="text-[10px] text-gray-400">sin historial completo</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleVerVersion(v.version)}
                          className={'text-xs px-2.5 py-1 rounded font-medium ' + (isSelected ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 hover:bg-gray-200 text-gray-600')}
                        >
                          {v.es_actual ? 'ver datos' : 'ver'}
                        </button>
                        {!v.es_actual && (
                          <button
                            onClick={() => handleVerCompletos(v.version)}
                            className="text-xs px-2 py-1 rounded font-medium bg-gray-50 hover:bg-gray-100 text-gray-500 border border-gray-200"
                          >
                            completos
                          </button>
                        )}
                        {v.restaurable && !v.es_actual && (
                          <button
                            onClick={() => handleRestaurar(v.version)}
                            className="text-xs px-2.5 py-1 rounded font-medium bg-amber-100 hover:bg-amber-200 text-amber-700"
                            disabled={cargando}
                          >
                            restaurar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Panel de comparativa debajo (siempre visible cuando hay reconstruccion) */}
          <div className="p-3">
            {reconstruccion ? (
              <div className="bg-gray-50 rounded border border-gray-200 text-xs">
                <h4 className="font-semibold text-gray-700 mb-2 px-3 pt-3">
                  Diferencias entre v{reconstruccion.historico?.version_solicitada} y v{reconstruccion.actual?.version_solicitada} (actual)
                </h4>
                {(cambios.length > 0 || cambiosChild.length > 0) ? (
                  <div className="overflow-y-auto px-3 pb-3" style={{maxHeight: '50vh'}}>
                    <table className="w-full text-left table-fixed">
                      <thead>
                        <tr className="text-[10px] text-gray-400 uppercase sticky top-0 bg-gray-50">
                          <th className="pr-2 py-1 w-2/5">Campo</th>
                          <th className="pr-2 py-1 w-3/10">Actual (v{reconstruccion.actual?.version_solicitada})</th>
                          <th className="py-1 w-3/10">v{reconstruccion.historico?.version_solicitada}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cambios.map(c => (
                          <tr key={c.key} className="border-t border-gray-100">
                            <td className="pr-2 py-1.5 font-medium text-gray-500 whitespace-nowrap text-[11px]">{c.key}</td>
                            <td className={'pr-2 py-1.5 text-gray-700 text-[11px] ' + (c.fullJson ? 'whitespace-pre-wrap break-all' : 'break-all')}>{c.fullJson ? c.actual : String(c.actual).slice(0, 120)}</td>
                            <td className={'py-1.5 text-amber-700 text-[11px] ' + (c.fullJson ? 'whitespace-pre-wrap break-all' : 'break-all')}>{c.fullJson ? c.anterior : String(c.anterior).slice(0, 120)}</td>
                          </tr>
                        ))}
                        {cambiosChild.map((c, i) => (
                          <tr key={`ch-${i}`} className="border-t border-gray-100">
                            <td className="pr-2 py-1.5 text-gray-500 text-[11px]">
                              {c.campo ? (
                                <span className="text-gray-400">{c.item === '(general)' ? `${NOMBRES_TABLAS[c.table] || c.table} › ${CHILD_FIELD_LABELS[c.campo] || c.campo}` : c.campo === '(cantidad)' ? NOMBRES_TABLAS[c.table] || c.table : `${c.item} › ${c.campo}`}</span>
                              ) : (
                                <>
                                  <span className="font-semibold">{NOMBRES_TABLAS[c.table] || c.table}</span>
                                  {c.item ? <><br/><span className="text-gray-400 ml-2">› {c.item}</span></> : ''}
                                </>
                              )}
                            </td>
                            <td className={'pr-2 py-1.5 text-gray-700 text-[11px] ' + ((c.campo === '(cantidad)' || (typeof c.actual === 'string' && c.actual.includes('\n'))) ? 'whitespace-pre-wrap break-all' : 'break-all')}>{formatearValor(c.actual)}</td>
                            <td className={'py-1.5 text-amber-700 text-[11px] ' + ((c.campo === '(cantidad)' || (typeof c.anterior === 'string' && c.anterior.includes('\n'))) ? 'whitespace-pre-wrap break-all' : 'break-all')}>{formatearValor(c.anterior)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 px-3 pb-3">Sin diferencias entre versiones.</p>
                )}
              </div>
            ) : selectedVersion && !cargando ? (
              <p className="text-gray-400 text-sm text-center py-8">Cargando datos de la versión...</p>
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">Seleccioná una versión para ver diferencias con la actual.</p>
            )}
          </div>
        </div>
      )}

      {/* Modal de versión completa */}
      {modalVersion !== null && modalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setModalVersion(null); setModalData(null); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
              <h3 className="text-sm font-semibold text-gray-700">Estado completo — v{modalVersion}</h3>
              <button onClick={() => { setModalVersion(null); setModalData(null); }} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto px-5 py-3 text-xs space-y-4 grow" style={{maxHeight: '70vh'}}>
              {/* Campos de obras */}
              {renderObraFields(modalData.obras).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-500 mb-1.5 uppercase tracking-wide text-[10px]">Datos de la obra</h4>
                  <table className="w-full text-left">
                    <tbody>
                      {renderObraFields(modalData.obras).map(f => (
                        <tr key={f.label} className="border-t border-gray-50">
                          <td className="pr-3 py-1 text-gray-400 whitespace-nowrap w-1/3 align-top">{f.label}</td>
                          <td className="py-1 text-gray-700 whitespace-pre-wrap break-all">{f.valor}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Tablas hijas */}
              {Object.entries(NOMBRES_TABLAS).map(([tKey, tLabel]) => {
                const rows = modalData[tKey];
                if (!rows) return null;
                const arr = Array.isArray(rows) ? rows : [rows];
                if (arr.length === 0 || (arr.length === 1 && typeof arr[0] === 'object' && Object.keys(arr[0]).length === 0)) return null;
                return (
                  <div key={tKey}>
                    <h4 className="font-semibold text-gray-500 mb-1.5 uppercase tracking-wide text-[10px]">{tLabel}</h4>
                    {arr.map((row, ri) => {
                      const entries = Object.entries(row).filter(([k]) => !['id', 'id_original', 'id_tabla_obras', 'from_cg_01_id_partido_obra', 'version_global', 'archived_at', 'operation', 'version', 'deleted_at'].includes(k));
                      if (entries.length === 0) return <p key={ri} className="text-gray-400 italic">(registro vacío)</p>;
                      return (
                        <div key={ri} className={'mb-2 ' + (arr.length > 1 ? 'border border-gray-100 rounded p-2' : '')}>
                          {arr.length > 1 && <p className="text-[10px] text-gray-400 mb-1 font-medium">Item {ri + 1}</p>}
                          <table className="w-full text-left">
                            <tbody>
                              {entries.map(([k, v]) => (
                                <tr key={k} className="border-t border-gray-50">
                                  <td className="pr-3 py-0.5 text-gray-400 whitespace-nowrap w-1/3 align-top">{k}</td>
                                  <td className="py-0.5 text-gray-700 whitespace-pre-wrap break-all">{formatearValorModal(v)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
